import { Injectable } from '@angular/core';
import { HygieneStaffReport } from './hygiene-staft-report.service';
import { map, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

const DEFAULT_DOMAIN = '';

export interface HygineLiveFilters {
  schooolOrCollege?: string;
  boardingType?: string;
}

export interface HygieneBuildingLocation {
  buildingId: number;
  buildingName: string;
  latitude: number;
  longitude: number;
  /** API field: "1" = photo taken (green), "0" = no photo (red) */
  hygienePhotoStatus?: string | number;
  hygienePhotoTaken?: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class HygineLiveService {

private readonly API_ORIGIN = String((environment as any).masterDomain || DEFAULT_DOMAIN).trim().replace(/\/+$/, '');
  private readonly staffReportUrl = `${this.API_ORIGIN}/apiV2/hygiene/staffReport`;
  private readonly staffReportPagedUrl = `${this.API_ORIGIN}/apiV2/hygiene/staffReportByPagenation`;
  private readonly buildingLocationsUrl = `${this.API_ORIGIN}/apiV2/hygiene/buildingLocations`;
  private readonly staffImagesUrl = `${this.API_ORIGIN}/employee/api/jwtV2/hygiene/getStaffImages`;

  private readonly tokenStorageKeys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;

  constructor(private http: HttpClient) {}

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': this.authHeaderValue,
      'Content-Type': 'application/json'
    });
  }

  private get authHeaderValue(): string {
    let raw: string | null = null;
    for (const key of this.tokenStorageKeys) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && raw.trim()) break;
    }
    return (raw || '').trim();
  }

  private collegeTypeQueryValue(schoolOrCollege: string): string {
    const u = (schoolOrCollege || '').toUpperCase();
    if (u === 'SCHOOL') return 'SCHOOL';
    if (u === 'COLLEGE') return 'COLLEGE';
    return schoolOrCollege;
  }

  private campusTypeQueryValue(boardingType: string): string {
    const u = (boardingType || '').toUpperCase();
    if (u === 'DAY_SCHOLAR') return 'DAYSCHOLAR';
    if (u === 'RESIDENTIAL') return 'RESIDENTIAL';
    return boardingType;
  }

  /** Convert YYYY-MM-DD to DD-MM-YYYY for API */
  private toDDMMYYYY(iso: string): string {
    if (!iso) return iso;
    const s = iso.toString().trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, d] = s.slice(0, 10).split('-');
      return [d, m, y].join('-');
    }
    return s;
  }

  /** Build query string with only college/school + boarding + date filters (no state/city/campus/building) */
  buildStaffReportQueryString(params?: {
    name?: string;
    schooolOrCollege?: string;
    boardingType?: string;
    filterType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): string {
    if (!params) return '';
    const apiParams: Record<string, string> = {};
    if (params['name']) apiParams['name'] = params['name'];
    if (params['schooolOrCollege']) {
      apiParams['collegeType'] = this.collegeTypeQueryValue(params['schooolOrCollege']);
    }
    if (params['boardingType']) {
      apiParams['campusType'] = this.campusTypeQueryValue(params['boardingType']);
    }
    if (params['filterType']) apiParams['filterType'] = params['filterType'];
    if (params['startDate'] != null && params['endDate'] != null) {
      apiParams['filterType'] = 'CUSTOM';
      apiParams['fromDate'] = this.toDDMMYYYY(params['startDate']);
      apiParams['toDate'] = this.toDDMMYYYY(params['endDate']);
    }
    if (params.page != null && Number.isFinite(Number(params.page))) {
      const uiPage = Math.floor(Number(params.page));
      if (uiPage >= 1) {
        apiParams['page'] = String(Math.max(0, uiPage - 1));
      }
    }
    if (params.pageSize != null && Number.isFinite(Number(params.pageSize)) && Number(params.pageSize) > 0) {
      apiParams['size'] = String(Math.floor(Number(params.pageSize)));
    }
    return Object.keys(apiParams).length ? '?' + new URLSearchParams(apiParams).toString() : '';
  }

  getStaffReport(params?: {
    name?: string;
    schooolOrCollege?: string;
    boardingType?: string;
    filterType?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<HygieneStaffReport> {
    const qs = this.buildStaffReportQueryString(params);
    return this.http.get<HygieneStaffReport>(`${this.staffReportUrl}${qs}`, { headers: this.headers });
  }

  getStaffReportByPagenation(params?: {
    name?: string;
    schooolOrCollege?: string;
    boardingType?: string;
    filterType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Observable<HygieneStaffReport> {
    const qs = this.buildStaffReportQueryString(params as any);
    return this.http.get<HygieneStaffReport>(`${this.staffReportPagedUrl}${qs}`, { headers: this.headers });
  }

  getBuildingLocations(params?: {
    schooolOrCollege?: string;
    boardingType?: string;
    filterType?: string;
  }): Observable<HygieneBuildingLocation[]> {
    const qs = this.buildStaffReportQueryString(params as any);
    return this.http
      .get<unknown>(`${this.buildingLocationsUrl}${qs}`, { headers: this.headers })
      .pipe(map((res) => this.normalizeBuildingLocations(res)));
  }

  /**
   * Initial Live Upload images for a date.
   * Backend: LocalDate.parse(date) → send ISO YYYY-MM-DD (e.g. 2026-07-24)
   */
  getStaffImages(hygieneDate: string): Observable<unknown> {
    const qs = new URLSearchParams();
    qs.set('hygieneDate', this.toIsoDate(hygieneDate));
    return this.http.get<unknown>(`${this.staffImagesUrl}?${qs.toString()}`, {
      headers: this.headers,
    });
  }

  /** Normalize to YYYY-MM-DD for LocalDate.parse. */
  private toIsoDate(value: string): string {
    if (!value) return value;
    const s = value.toString().trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
      const [d, m, y] = s.split('-');
      return `${y}-${m}-${d}`;
    }
    return s;
  }

  normalizeBuildingLocations(res: unknown): HygieneBuildingLocation[] {
    const rawList = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data as HygieneBuildingLocation[])
        : [];

    return rawList.map((item) => {
      const record = item as Record<string, unknown>;
      return {
        ...(item as HygieneBuildingLocation),
        hygienePhotoTaken: this.resolveHygienePhotoTaken(record),
      };
    });
  }

  private resolveHygienePhotoTaken(loc: Record<string, unknown>): boolean {
    const status = loc['hygienePhotoStatus'];
    if (status === 1 || status === '1') {
      return true;
    }
    if (status === 0 || status === '0') {
      return false;
    }

    const raw = loc['hygienePhotoTaken'];
    if (typeof raw === 'boolean') {
      return raw;
    }
    if (raw === 1 || raw === '1') {
      return true;
    }
    if (raw === 0 || raw === '0' || raw == null || raw === '') {
      return false;
    }
    const normalized = String(raw).trim().toLowerCase();
    return normalized === 'true' || normalized === 'yes' || normalized === 'y';
  }
}
